import {toast} from "react-toastify";
import {useBulkDeleteDriverOperationalNotesMutation, useBulkUpsertDriverOperationalNotesMutation, useDeleteDriverOperationalNoteMutation, useLoadManyDriverOperationalNotesLazyQuery, useLoadSingleDriverOperationalNoteLazyQuery, useUpsertDriverOperationalNoteMutation} from "../../../__generated__/generated_types";
import {useEffect, useState} from "react";
import {DriverNote} from "../DriverNoteModal";
import {UseBulkDriverNoteModal} from "./useBulkWsDriverNoteModal";
import {BulkDriverNoteModalProps} from "../BulkDriverNoteModal";


interface BulkOpDriverHookProps {
    onSave?: () => void;
}

export default function useBulkOpDriverNoteModal(props?: BulkOpDriverHookProps): UseBulkDriverNoteModal {
    const [driverNoteData, setDriverNoteData] = useState<DriverNote[] | null>(null);

    const [fetchNotesLocal, {data: noteData, loading: noteLoading}] = useLoadManyDriverOperationalNotesLazyQuery({fetchPolicy: "no-cache"});

    const [deleteNotes, {loading: noteDeleting}] = useBulkDeleteDriverOperationalNotesMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted(data) {
            if (data.bulkDeleteDriverOperationalNotes > 0) {
                toast.success("Notes deleted");
                props?.onSave?.();
            }
        },
    });

    const [upsertNotes, {loading: noteSaving}] = useBulkUpsertDriverOperationalNotesMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted(data) {
            if (data.bulkUpsertDriverOperationalNotes) {
                toast.success("Notes updated");
                props?.onSave?.();
            }
        },
    });

    useEffect(() => {
        const notes = noteData?.loadManyDriverOperationalNotes;
        if (notes) {
            setDriverNoteData(prev => {
                if (!prev) {
                    return [];
                }
                const mergedNoteData = [];
                for (const dNote of prev) {
                    const existingNoteRecord = notes.find(note => note.lineId === dNote.lineId);
                    mergedNoteData.push({
                        ...dNote,
                        lineName: existingNoteRecord?.lineName ?? "",
                        note: existingNoteRecord?.note ?? "",
                        isReadOnly: existingNoteRecord?.isReadOnly ?? false,
                    });
                }
                return mergedNoteData;
            });
        }
    }, [noteData]);

    // user experience should be the same whether they are creating or updating a note, so a
    // partial driverNote object is still valid to launch the modal. If the note exists, then as the fetch
    // gets data back it will populate the rest of the object.
    function fetchNotes(lineIds: string[]): void {
        const builtNoteData: DriverNote[] = lineIds.map(lineId => ({
            lineType: "operational",
            lineId: lineId,
            lineName: "",
            lineValuesLocked: false,
            note: null,
            isReadOnly: null,
        }));

        setDriverNoteData(builtNoteData);

        fetchNotesLocal({
            variables: {
                driverOperationalIds: lineIds,
            },
        });
    }

    // I figured if the note theyre trying to save is empty, we could just go ahead and delete
    // the note record out of the DB, but if that seems like too heavy of an assumption we can make it an explicit interaction
    function save(driverNotes: DriverNote[]): void {
        const firstDriver = driverNotes[0];
        if (!firstDriver) {
            return;
        }

        // when setting notes in bulk, they all get the same note and isReadOnly values
        // so we pull those from the first driver in the array
        const noteText = typeof firstDriver.note === "string" ? firstDriver.note.trim() : "";
        const isReadOnly = !!firstDriver.isReadOnly;
        const doingDelete = noteText === "";

        if (doingDelete) {
            deleteNotes({
                variables: {
                    driverOperationalIds: driverNotes.map(dNote => dNote.lineId),
                },
            });
        } else {
            upsertNotes({
                variables: {
                    lineIds: driverNotes.map(dNote => dNote.lineId),
                    note: noteText,
                    isReadOnly: isReadOnly,
                },
            });
        }
    }

    function reset(): void {
        setDriverNoteData(null);
    }

    return {
        driverNoteData: driverNoteData,
        isBusy: noteLoading || noteSaving || noteDeleting,
        fetchNotes: fetchNotes,
        save: save,
        reset: reset,
    };
}
